/** ECS component stores (bitECS 0.4, structure-of-arrays), indexed by entity id. */
export const MAX_ENTITIES = 4096

const f64 = (): Float64Array => new Float64Array(MAX_ENTITIES)
const f32 = (): Float32Array => new Float32Array(MAX_ENTITIES)
const u8 = (): Uint8Array => new Uint8Array(MAX_ENTITIES)
const i32 = (): Int32Array => new Int32Array(MAX_ENTITIES)

/**
 * Vector columns are class instances, never `{ x: f64(), … }` object literals. V8 gives every object literal with
 * the same keys in the same order one hidden-class chain, so a literal holding typed arrays under `x`, `y`, `z`
 * (`w`) turns those fields of every `{ x: 0, y: 0, z: 0 }` scratch vector and quaternion into tagged ones, and then
 * each non-integer float stored into a scratch allocated a 16 B heap number (the traffic hot loop's garbage; gate A).
 */
class Columns3<T> {
  readonly x: T
  readonly y: T
  readonly z: T
  constructor(make: () => T) {
    this.x = make()
    this.y = make()
    this.z = make()
  }
}

class Columns4<T> {
  readonly x: T
  readonly y: T
  readonly z: T
  readonly w: T
  constructor(make: () => T) {
    this.x = make()
    this.y = make()
    this.z = make()
    this.w = make()
  }
}

/** True sector position in float64 (D-011). Never hand these to three.js or Rapier directly. */
export const Pos64 = new Columns3(f64)
export const PrevPos64 = new Columns3(f64)
export const Rot = new Columns4(f32)
export const PrevRot = new Columns4(f32)
export const Vel = new Columns3(f32)
export const AngVel = new Columns3(f32)
/** Pilot intent, written by player input or AI and read by flight and combat (D-013). */
export const Control = {
  surge: f32(),
  sway: f32(),
  heave: f32(),
  pitch: f32(),
  yaw: f32(),
  roll: f32(),
  boost: u8(),
  firePrimary: u8(),
  fireSecondary: u8(),
}
/**
 * The ACTUAL output of each axis as a fraction (-1..1) of that axis's limit, written by the flight
 * step every tick (force or torque on the local axis ÷ its limit; forward uses the boosted limit while
 * boosting). Signs follow `Control`. Ship views and audio read it; nobody else writes it.
 */
export const Thrust = { surge: f32(), sway: f32(), heave: f32(), pitch: f32(), yaw: f32(), roll: f32() }
export const Ship = { defIndex: u8(), faOn: u8(), boosting: u8() }
export const Energy = { value: f32() }
/** 0 = player side, 1 = raiders. Friendly fire is off in M1 (plan §2.5). */
export const Team = { id: u8() }
export const Hull = { value: f32() }
/** `lastHit` is sim seconds (float64); initialised to -1e9 so a fresh ship regenerates at once. */
export const Shield = { value: f32(), lastHit: f64() }
/** `primaryIndex` = selected primary slot (0/1); `alternate` = next muzzle in the alternation order. */
/** `assist`: aim-assist strength (the level multiplier from weapons.json; the player's follows the setting). */
export const Weapons = { primaryIndex: u8(), cooldownPrimary: f32(), cooldownSecondary: f32(), alternate: u8(), torpedoes: u8(), assist: f32() }
/** Selected target entity, -1 = none. */
export const Target = { eid: i32() }
/** Torpedo lock: `held` seconds inside the lock cone, `locked` once it reaches the weapon's lock time. */
export const Lock = { target: i32(), held: f32(), locked: u8() }
/** AI pilot storage. G4 owns the semantics; `aim*` is the sample-and-hold target state (reaction delay). */
export const AIPilot = {
  state: u8(),
  profile: u8(),
  target: i32(),
  stateTimer: f32(),
  jinkX: f32(),
  jinkY: f32(),
  aimX: f64(),
  aimY: f64(),
  aimZ: f64(),
  aimVX: f32(),
  aimVY: f32(),
  aimVZ: f32(),
  sampleTimer: f32(),
  lastAttacker: i32(),
}
/** `respawnAt` is sim seconds (float64). */
export const Life = { alive: u8(), respawnAt: f64() }
/** Tag component. */
export const Player = {}
